import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { beatArrivalOnce } from "@/lib/pulse";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AppErrorBoundary from "@/components/ui/AppErrorBoundary";
// « La Veillée » a son propre filet, au-dessus de son chunk paresseux — la
// plaque racine est ivoire (juste pour un invité, muette pour un opérateur) ;
// celle du mur parle le vocabulaire du mur et, sous ?debug=1, dit CE QUI a
// lâché. Elle vit dans le bundle principal, donc elle survit à un chunk qui
// ne se télécharge pas. Voir components/ui/WallErrorBoundary.jsx.
import WallErrorBoundary from "@/components/ui/WallErrorBoundary";
import { AuthProvider } from "@/context/AuthContext";
import { t, isLangLoaded } from "@/lib/i18n";
import { useIsMobile, matchesMobileViewport } from "@/lib/viewport";
import { siteEnabled } from "@/lib/siteGate";
import SiteBientot from "@/pages/SiteBientot";
import { parseSitePath, siteToLegacy } from "@/lib/siteRoutes";
import { mountsExperience } from "@/lib/bootLang";
import { accueilAlreadySeen } from "@/lib/accueilSeen";
// A phone in front of a page that only the wide screen renders (a tale, the
// Library, a suite): the address is KEPT and the note says so — in the main
// bundle, because it weighs a few lines and a phone never downloads the site.
import WideScreenOnly from "@/pages/WideScreenOnly";

// Route-level code splitting: every edition loads on demand, the guest's
// phone experience included.
//
// ⚑ L'EXPÉRIENCE TÉLÉPHONE ÉTAIT LA SEULE IMPORTÉE EN DUR (audit du
// 10/09/2026). Neuf éditions étaient paresseuses ; celle-ci ne l'était pas,
// et le tronc commun — ce que CHAQUE visiteur du site télécharge, sur
// chacune des 2 317 adresses — portait l'application entière : le Livre,
// l'Œil, l'Explorer, les Balades, 459 Ko minifiés que jamais un écran large
// n'exécute. Mesuré : 44 % de main.js exécuté sur l'accueil du site, le
// reste analysé et compilé pour rien, dans une tâche longue de 273 à 424 ms
// sous CPU ×4.
//
// La décision existe déjà plus bas (`PickEntry` : téléphone ou grille fermée
// → l'app, sinon le site) ; l'import la suit désormais. Un téléphone paie un
// aller-retour de plus (son chunk, après le tronc) — on le lui rend en
// partie en lançant la demande dès l'évaluation du module, avant même le
// premier rendu : voir `wantsExperience` ci-dessous.
const loadExperience = () => import(/* webpackChunkName: "experience" */ "@/pages/MedardExperience");
const MedardExperience = lazy(loadExperience);

// The same question `PickEntry` asks, answerable before React mounts: does
// this visitor get the guest app? If so, its chunk is asked for NOW, so the
// request overlaps React's boot instead of waiting for the first render.
// `?display=` and `/livre` also land on the app; they are rarer, and lose
// nothing but that head start.
//
// ⚑ ET BORNÉE AUX ADRESSES QUI MONTENT L'EXPÉRIENCE (audit 10/09 bis, perf
// n°5, réserve) : un téléphone sur /admin, /chantier, /reveil, /kiosk, la
// notice légale seule ou une vitrine `?display=` ne la monte jamais — la
// demander là, c'est 117 Ko gzippés contre la page demandée. La règle vit
// dans lib/experienceRoutes.json, la même que lit l'indice de la coquille.
export function wantsExperience() {
    // ⚑ PORTE FERMÉE (15/09/2026) : personne ne préfabrique l'application.
    // La règle disait `matchesMobileViewport() || !siteEnabled()` — écrite
    // quand « site fermé » voulait dire « on sert l'app à la place ». Depuis
    // que « fermé » veut dire « page d'attente », elle demandait **117 Ko
    // gzippés d'application à chaque visiteur qui ne verra qu'un blason et un
    // champ** : le plus lourd du paquet, pour le plus léger des écrans.
    // Les installations (`?display=…`, `/livre`) y perdent leur avance de
    // quelques centaines de millisecondes — ce que le commentaire ci-dessus
    // accepte déjà pour elles, et ce sont des écrans fixes.
    if (!siteEnabled()) return false;
    if (!matchesMobileViewport()) return false;
    return mountsExperience(window.location.pathname, window.location.search);
}
if (typeof window !== "undefined" && wantsExperience()) {
    loadExperience();
}
// The transparent-OLED vitrine is a PIECE OF FURNITURE in the lobby, reached
// only by /kiosk or ?display=transparent. It was imported eagerly, so every
// guest's phone downloaded the kiosk screen — and, through it, the whole QR
// generator — for a screen their device can never be.
const MedardKioskScreen = lazy(() => import(/* webpackChunkName: "kiosk" */ "@/pages/MedardKioskScreen"));
// The « TV de suite » edition — the Samsung Frame on each suite's wall,
// reached only by ?display=suite. Its own lazy chunk for the same reason as
// the kiosk: no guest phone should ever download the TV scene.
const SuiteScreen = lazy(() => import(/* webpackChunkName: "suite-tv" */ "@/pages/SuiteScreen"));
// « La Veillée » — the suite-wall projected book, reached only by
// ?display=conte. Its own lazy chunk for the same reason as the kiosk and
// the TV: no guest phone downloads the wall edition.
const ProjectionConteScreen = lazy(() => import(/* webpackChunkName: "projection" */ "@/pages/ProjectionConteScreen"));
// LE SITE — the real website a desktop visitor lands on (Steven, 06/09/2026;
// memory/strategie/SITE_WEB_DESKTOP.md). Its own chunk: a phone never
// downloads a page it can never show, exactly like the kiosk.
const SiteApp = lazy(() => import(/* webpackChunkName: "site" */ "@/pages/site/SiteApp"));
// The legal notice ALONE — the one site page a phone renders (LCEN: it must be
// reachable from every device, and the app has no legal page of its own;
// audit 10/09/2026, mobile n°1). Its own small chunk: a phone pays the legal
// text, never the website.
const SiteMentionsLegalesSeules = lazy(() => import(/* webpackChunkName: "mentions" */ "@/pages/site/SiteMentionsLegalesSeules"));
// ⚑ STATIQUE, et c'est un choix : tant que la façade est fermée, cette page
// est ce que voit CHAQUE visiteur. La différer ajouterait un aller-retour
// avant le premier pixel, sur la seule page qu'on sert. Elle est petite — un
// bloc d'identité et un champ — et le blason qu'elle affiche voyage déjà.
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
// « Réveillez Médard » — the card-AR pilot. Route-lazy so the main app never
// pays for it; the heavy MindAR/three stack loads only on camera start.
const ReveilExperience = lazy(() => import("@/pages/ReveilExperience"));
// « Le Chantier » — the PRIVATE construction-tracking module (staff only,
// owner request 26/07). Both pages share ONE named lazy chunk: the guest
// bundle pays ~0 bytes for a tool guests will never see.
const ChantierLogin = lazy(() => import(/* webpackChunkName: "chantier" */ "@/pages/ChantierLogin"));
const ChantierBoard = lazy(() => import(/* webpackChunkName: "chantier" */ "@/pages/ChantierBoard"));

// Best-effort language for the pre-app fallback (no app state exists yet) —
// and only a language whose dictionary is LOADED (one chunk per language,
// audit 10/09 bis): `t()` would answer in French for any other.
function fallbackLang() {
    if (typeof navigator !== "undefined") {
        const code = (navigator.language || "").slice(0, 2).toLowerCase();
        if (isLangLoaded(code)) return code;
    }
    return "fr";
}

// The shell's ground for the screen the phone is ABOUT to show, so the wait
// for the « experience » chunk is that screen's colour and nothing else.
export const FALLBACK_TONE = Object.freeze({
    // A wide screen: the site (and the Book's parchment is close enough) —
    // the hairline spinner stays.
    ivory: "fixed inset-0 flex items-center justify-center bg-aman-ivory",
    // A phone's first launch: the welcome veil « Médard vous accueille » is
    // navy (MedardAccueil, bg-manoir-delft-deep). Same ground, no spinner.
    navy: "fixed inset-0 bg-manoir-delft-deep",
    // A phone that has seen the veil lands in the tab shell, on parchment.
    parchment: "fixed inset-0 bg-aman-parchment",
});

/**
 * The loading state between the trunk and a lazy edition.
 *
 * ⚑ SUR TÉLÉPHONE, LA COULEUR DU RITUEL (audit 10/09 bis, perf, réserve du
 * sceptique). Mesuré à froid : 300 à 560 ms de plaque ivoire et d'anneau qui
 * tourne AVANT le voile navy « Réveiller Médard » — un écran étranger au
 * rituel, vu par chaque premier arrivant. Le repli prend désormais le fond
 * de l'écran qui vient (navy au premier lancement, parchemin ensuite), sans
 * anneau : rien n'est plus long, rien ne tourne, l'attente est muette.
 * Un écran large garde l'ivoire et son anneau.
 */
export function fallbackTone() {
    if (typeof window === "undefined" || !wantsExperience()) return "ivory";
    return accueilAlreadySeen() ? "parchment" : "navy";
}

function RouteFallback() {
    const tone = fallbackTone();
    return (
        <div
            className={FALLBACK_TONE[tone]}
            role="status"
            aria-label={t(fallbackLang(), "loading")}
            data-testid="route-fallback"
            data-tone={tone}
        >
            {tone === "ivory" && (
                <div
                    aria-hidden="true"
                    className="w-10 h-10 rounded-full border border-aman-hairline-deep border-t-aman-ink motion-safe:animate-spin"
                />
            )}
        </div>
    );
}

// `/livre` — the lobby Book edition under a readable address (SITE_WEB_DESKTOP
// §2 D3). Strictly the same tree as `?display=book`: splash forced, URL kept
// as configuration. The Book loses not a byte; it changes door. A language
// prefix (`/en/livre`, the site's own way of writing it) opens the same door.
const isLivrePath = (pathname) => parseSitePath(pathname).name === "livre";

// A site address rewritten for the phone keeps what the site did not
// translate — `?src=` above all, the printed QR's tag that lib/pulse counts.
// The page's own query is translated WITH it (siteRoutes SITE_PAGE_QUERY):
// `/cite-antique?monument=x` → `/?page=cite-antique&monument=x`, the cité
// flying to the same monument on the phone.
// Returns null when the page has NO address in the app (SITE_WIDE_ONLY): the
// caller then keeps the address instead of rewriting it in silence.
function legacyAddressFor(parsed, search) {
    const target = siteToLegacy({ ...parsed, search });
    if (target === null) return null;
    const keep = new URLSearchParams(search);
    keep.delete("site");
    keep.delete("lang");
    if (![...keep.keys()].length) return target;
    const [path, query = ""] = target.split("?");
    const merged = new URLSearchParams(query);
    keep.forEach((v, k) => { if (!merged.has(k)) merged.set(k, v); });
    return `${path}?${merged.toString()}`;
}

/**
 * The entry switch. Order of precedence, and it matters:
 *   1. the physical editions, by `?display=` (and legacy `?kiosk=1`) — they
 *      are query params on `/`, so they MUST be read before any site logic
 *      (the tablet driving the transparent LCD carries `?session=&k=`; a
 *      redirection would strip it and lose the room, 19/08 review);
 *   2. `/livre` — the lobby Book edition;
 *   3. a phone (or the site gate closed) → the guest experience. A site path
 *      opened on a phone is first rewritten into the app's own contract
 *      (`?monument=…`), so a link sent from the site opens the same fiche;
 *   4. a wide screen with the gate open → the website.
 *
 * `?display=` switch — the reserved namespace for the physical editions:
 *   · transparent → kiosk screen (transparent OLED vitrine; `?kiosk=1` legacy)
 *   · book        → lobby « Book » edition: the normal experience with the
 *                    splash FORCED ON even on phone-sized viewports (guests'
 *                    own phones skip the splash; the lobby ritual keeps it)
 *   · tablet      → the tablet that DRIVES the transparent LCD vitrine: the
 *                    normal experience, splash and all, whose URL carries the
 *                    installation's fixed room
 *                    (`&session=<id>&k=<jeton>`, MIGRATION_NOTES.md). Nothing
 *                    renders differently — the flag exists so the URL is NOT
 *                    stripped, because here it is CONFIGURATION and not a
 *                    guest's address bar (see `installation` below)
 *   · suite       → in-suite TV (Samsung Frame): the permanent pairing
 *                    invitation (`&suite=<id>&lang=<code>`)
 *   · conte       → « La Veillée »: the suite-wall projected book — a giant
 *                    open book on true black, narrated by Médard
 *                    (`&conte=<id>&lang=<code>`; `&turn=flat` forces instant
 *                    page swaps, `&bow=flat` lays the resting pages' TYPE
 *                    flat and `&slices=N` thins the curve, on weak
 *                    projection sticks). This IS the
 *                    Phase 4 `projection` reservation, implemented under the
 *                    name of what it shows; `?display=projection` stays free.
 */
function PickEntry() {
    const isMobile = useIsMobile();
    if (typeof window === "undefined") return <MedardExperience />;
    const u = new URLSearchParams(window.location.search);
    const display = u.get("display");
    if (display === "transparent" || u.get("kiosk") === "1") {
        return <MedardKioskScreen />;
    }
    if (display === "suite") {
        return <SuiteScreen />;
    }
    if (display === "conte") {
        return (
            <WallErrorBoundary>
                <ProjectionConteScreen />
            </WallErrorBoundary>
        );
    }
    // `installation` = « this URL is the configuration of a fixed screen,
    // never a bar a guest might copy ». It is what keeps `?session=&k=`
    // in the address bar, so a kiosk browser restoring its last address
    // comes back into the SAME room. Both fixed editions carry it; only
    // the Book edition also forces the splash.
    if (display === "book" || isLivrePath(window.location.pathname)) {
        return <MedardExperience forceSplash installation />;
    }
    if (display === "tablet") {
        return <MedardExperience installation />;
    }
    // ⚑ LA FAÇADE EST REFERMÉE (Steven, 15/09/2026) — « c'est pas encore
    // ouvert ». Tant que la garde est fermée, TOUT écran voit la page
    // d'attente : c'est le choix de Steven, le téléphone compris, puisque
    // l'app est la démonstration d'un hôtel qui n'accueille encore personne.
    //
    // ⚠ DEUX EXCEPTIONS, et elles sont de nature différente :
    //   · `/mentions-legales` — obligation légale (LCEN) : les mentions
    //     doivent rester atteignables depuis toute page publique, porte
    //     fermée comprise. Elles passent AVANT la garde, jamais après.
    //   · les écrans d'installation (`?display=…`, `/livre`) sont traités
    //     plus haut dans cette fonction, et /admin /chantier /kiosk /reveil
    //     sont des routes à part avec leur propre authentification. *La
    //     garde ferme la VITRINE, pas la porte de service.*
    if (!siteEnabled()) {
        const ferme = parseSitePath(window.location.pathname);
        if (ferme.name === "mentionsLegales") {
            return <SiteMentionsLegalesSeules lang={ferme.lang} />;
        }
        return <SiteBientot lang={ferme.lang} />;
    }
    if (isMobile) {
        const parsed = parseSitePath(window.location.pathname);
        // The legal notice renders at EVERY width: it is plain text, and the
        // app has no page for it. Before 10/09/2026 a phone on
        // `/mentions-legales` landed on the app's home — the LCEN mentions
        // were unreachable from the whole phone audience (audit, mobile n°1).
        if (parsed.name === "mentionsLegales") {
            return <SiteMentionsLegalesSeules lang={parsed.lang} />;
        }
        // A site address on a phone (a link sent from the website, a search
        // result): the app opens on the SAME content, by its own contract.
        // (`replaceState` in the render body, on purpose: MedardExperience
        // reads the URL in its `useState` initialisers, so the address must
        // already be the app's when it mounts. Idempotent under StrictMode.)
        if (parsed.name && window.location.pathname !== "/") {
            const target = legacyAddressFor(parsed, window.location.search);
            // No equivalent at all (a tale, the Library, a suite): the address
            // is KEPT and the page says where it is read — never a silent `/`.
            if (target === null) return <WideScreenOnly lang={parsed.lang} />;
            window.history.replaceState({}, "", target);
        }
        return <MedardExperience />;
    }
    return <SiteApp />;
}

function App() {
    // « Combien de scans ? » — a printed QR tags its URL with `?src=<slug>`
    // (carte de lit, TV, billet du petit train). One anonymous beat per
    // browsing session, whatever the route or edition; see lib/pulse.js.
    useEffect(() => { beatArrivalOnce(); }, []);
    return (
        <div className="App">
          <AppErrorBoundary>
            <BrowserRouter>
                <AuthProvider>
                    <Suspense fallback={<RouteFallback />}>
                        <Routes>
                            <Route path="/kiosk" element={<MedardKioskScreen />} />
                            <Route path="/reveil" element={<ReveilExperience />} />
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="/chantier/login" element={<ChantierLogin />} />
                            <Route path="/chantier" element={<ChantierBoard />} />
                            {/* Legacy route — the five signature itineraries now live
                                inside the Balade chapter (dedupe #1: « Balades » is
                                the ONE walking experience). Old links / QR codes land
                                on the Book opened on that chapter. */}
                            <Route path="/itineraires" element={<Navigate to="/?page=balade&mode=signature" replace />} />
                            {/* Everything else — `/`, the site's addresses, `/livre`,
                                and any unknown path (there used to be NO catch-all:
                                an unknown URL rendered a blank page while the server
                                had already answered with the shell). */}
                            <Route path="/*" element={<PickEntry />} />
                        </Routes>
                    </Suspense>
                </AuthProvider>
            </BrowserRouter>
          </AppErrorBoundary>
        </div>
    );
}

export default App;
